import { colorCodesForAvatar } from "../constants/index";
import { chracterPosition } from "./index";

function mapApiGroupsToSidebarGroups(groups) {
  if (!Array.isArray(groups)) return [];
  return groups.map((g) => ({
    _id: g._id,
    title: g.name || g.title,
    group_image: g.image || g.group_image,
    agents: g.agents,
  }));
}

function buildGroupsArray(t, ticketIdFallback) {
  if (Array.isArray(t.groups) && t.groups.length) {
    return mapApiGroupsToSidebarGroups(t.groups);
  }
  const g = t.group;
  if (g?.name) {
    return [
      {
        _id: g._id || `tv2-group-${ticketIdFallback || "x"}`,
        title: g.name,
        group_image: g.image || g.group_image || null,
        agents: typeof g.members === "number" ? g.members : g.agents ?? 0,
      },
    ];
  }
  return [];
}

export function ticketShortId(ticket) {
  const id = ticket?._id;
  if (!id || typeof id !== "string") return "";
  return id.slice(-6);
}

function formatListTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const days = Math.floor(diffMs / (86400000));
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(diffMs / 3600000);
  if (hours >= 1) return `${hours}h`;
  const mins = Math.floor(diffMs / 60000);
  if (mins >= 1) return `${mins}m`;
  return "Now";
}

function formatDetailDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function capitalizePriority(p) {
  if (!p || typeof p !== "string") return "";
  const s = p.trim().toLowerCase();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Merge list row + full ticket payload into the shape TicketV2 UI expects. */
export function buildTv2Ticket(listRow, detailTicket, explicitId) {
  const t = detailTicket && Object.keys(detailTicket).length ? detailTicket : listRow || {};
  const _id = t._id || explicitId || listRow?._id;
  const sid = ticketShortId(t);
  const updatedAt = t.updatedAt ?? t.updated_at;
  const createdAt = t.createdAt ?? t.created_at ?? t.createdAt;
  const subject = t.subject || "No subject";
  const customerName = t.customerName || "Unknown";
  const customerEmail = t.customerEmail || "";
  const preview =
    (typeof t.lastMessage === "string" && t.lastMessage) ||
    (typeof t.description === "string" ? t.description.slice(0, 80) : "") ||
    subject;
  const createdByName =
    t.createdBy?.name ||
    t.createdBy?.full_name ||
    t.created_by?.name ||
    customerName;
  const createdByEmail =
    t.createdBy?.email || t.created_by?.email || customerEmail || "—";

  const tagLabels = Array.isArray(t.tags)
    ? t.tags.map((x) => (typeof x === "string" ? x : x?.name || x?.label || "")).filter(Boolean)
    : [];

  const group = t.group
    ? {
        _id: t.group._id,
        name: t.group.name || t.group.title,
        image: t.group.image,
        group_image: t.group.group_image,
        members: t.group.members ?? t.group.agents,
        agents: t.group.agents,
      }
    : null;

  const groups = buildGroupsArray(t, _id);

  const requester =
    customerName || customerEmail
      ? {
          name: customerName,
          email: customerEmail,
          avatar: t.requester?.avatar || null,
        }
      : null;

  const history = [];
  if (subject) history.push(subject);
  if (typeof t.lastMessage === "string" && t.lastMessage && t.lastMessage !== subject) {
    history.push(t.lastMessage.length > 80 ? `${t.lastMessage.slice(0, 80)}…` : t.lastMessage);
  }

  return {
    id: sid,
    _id,
    customerName,
    customerEmail,
    avatar: t.customerAvatar || t.avatar || null,
    preview,
    time: formatListTime(updatedAt),
    unread: Boolean(t.unread),
    subject,
    status: (t.status || "open").toLowerCase(),
    priority: capitalizePriority(t.priority || ""),
    tags: tagLabels,
    source: t.source || t.channel || "—",
    created: formatDetailDate(createdAt),
    lastMessage: formatDetailDate(updatedAt),
    requester,
    ccEmails: Array.isArray(t.ccEmails) ? t.ccEmails : [],
    group,
    groups,
    history: history.length ? history : [subject],
    createdBy: {
      name: createdByName,
      email: createdByEmail,
    },
    assignee: t.assignee || null,
    raw: t,
  };
}

function msgTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
    .toLowerCase();
}

export function mapTicketMessagesToTv2(messages, customerName) {
  if (!Array.isArray(messages)) return [];
  const sorted = [...messages].sort((a, b) => {
    const ta = new Date(a.created_at || a.createdAt || 0).getTime();
    const tb = new Date(b.created_at || b.createdAt || 0).getTime();
    return ta - tb;
  });
  const name = customerName || "User";
  const initial = name.substring(0, 1).toUpperCase();
  const userAvatarColor =
    colorCodesForAvatar[chracterPosition(initial)] || "#757575";

  return sorted.map((m, index) => {
    const isAgent = m.type === "agent";
    const isPrivate = m.isPrivate === true;
    const text = m.message || "";
    const created = m.created_at || m.createdAt;
    const key = m._id || m.id || `m-${index}-${created}`;

    const agent = m.agent || {};
    const agentImg = agent.agent_image || m.file_image || null;
    const agentInitial = (agent.full_name || m.sender || "A").substring(0, 1).toUpperCase();

    let type = "received";
    let avatar = null;
    if (isAgent) {
      if (isPrivate) type = "internal";
      else type = "sent";
      avatar = agentImg || undefined;
    } else {
      type = "received";
      avatar = null;
    }

    const firstUrl = m.attachments?.[0]?.url || m.file_image;
    const attachment =
      firstUrl && m.attachments?.[0]
        ? {
            name: m.attachments[0].filename || "Attachment",
            size: "",
          }
        : undefined;

    return {
      id: key,
      type,
      text,
      time: msgTime(created),
      avatar,
      userAvatarColor,
      userInitial: initial,
      senderInitial: agentInitial,
      attachment,
    };
  });
}
